/**
 * HelpPage – Hướng dẫn sử dụng ứng dụng In Ấn Chứng Từ.
 * Bao gồm: quy trình A-Z, cú pháp template, danh sách mẫu có sẵn.
 */
import * as React from "react";
import { TooltipProvider } from "@/components/ui/tooltip";
import { LanguageSwitcher, useTranslation } from "@/i18n";
import {
  Printer,
  ChevronRight,
  Building2,
  ArrowLeft,
  HelpCircle,
  FileText,
  ListOrdered,
  Braces,
  Table2,
  Hash,
  Type,
  Package,
  Truck,
  FileSpreadsheet,
  Receipt,
  PackageOpen,
  Wallet,
  PackagePlus,
  Undo2,
} from "lucide-react";
import { PRESET_TEMPLATES } from "@/utils/presetTemplates";

interface HelpPageProps {
  onBack: () => void;
}

/** Map preset id → icon (mirrors the picker). */
function presetIcon(id: string) {
  switch (id) {
    case "phieu-giao-hang": return Truck;
    case "bao-gia": return FileSpreadsheet;
    case "hoa-don": return Receipt;
    case "phieu-xuat-kho": return PackageOpen;
    case "phieu-luong": return Wallet;
    case "phieu-nhap-kho": return PackagePlus;
    case "phieu-tra-hang": return Undo2;
    default: return FileText;
  }
}

function Section({ icon: Icon, title, children }: { icon: React.ComponentType<{ className?: string }>; title: string; children: React.ReactNode }) {
  return (
    <section className="rounded-lg border bg-card shadow-sm">
      <header className="flex items-center gap-2 border-b bg-muted/20 px-4 py-2.5">
        <Icon className="h-4 w-4 text-primary" />
        <h2 className="text-sm font-semibold">{title}</h2>
      </header>
      <div className="space-y-3 p-4 text-sm leading-relaxed">{children}</div>
    </section>
  );
}

function Code({ children }: { children: React.ReactNode }) {
  return (
    <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-[12px] text-primary">
      {children}
    </code>
  );
}

function SyntaxRow({ token, desc }: { token: string; desc: string }) {
  return (
    <div className="flex flex-col gap-0.5 rounded-md border bg-muted/20 px-3 py-2 sm:flex-row sm:items-center sm:gap-3">
      <Code>{token}</Code>
      <span className="text-xs text-muted-foreground">{desc}</span>
    </div>
  );
}

export default function HelpPage({ onBack }: HelpPageProps) {
  const { t } = useTranslation();

  return (
    <TooltipProvider>
      <div className="min-h-screen bg-[hsl(210_20%_98%)] text-foreground">
        <div className="h-0.5 w-full bg-primary" aria-hidden />

        <header className="sticky top-0 z-50 w-full border-b border-border/80 bg-background/95 shadow-sm backdrop-blur supports-[backdrop-filter]:bg-background/90">
          <div className="mx-auto flex h-14 max-w-[1200px] items-center justify-between gap-4 px-3 md:px-6">
            <div className="flex min-w-0 items-center gap-3">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary text-primary-foreground shadow-sm ring-1 ring-black/5">
                <HelpCircle className="h-[18px] w-[18px]" />
              </div>
              <div className="min-w-0 flex flex-col gap-0.5">
                <h1 className="truncate text-[15px] font-semibold tracking-tight leading-none">
                  Hướng Dẫn Sử Dụng
                </h1>
                <nav className="flex items-center gap-1 text-[11px] text-muted-foreground" aria-label="Breadcrumb">
                  <Building2 className="h-3 w-3 shrink-0 opacity-70" />
                  <span className="font-medium text-foreground/70">BIZBOX TOOLS</span>
                  <ChevronRight className="h-3 w-3 shrink-0 opacity-50" />
                  <span className="truncate">{t("app.enterpriseEdition")}</span>
                  <ChevronRight className="h-3 w-3 shrink-0 opacity-50" />
                  <span className="truncate text-foreground/80">Hướng dẫn</span>
                </nav>
              </div>
            </div>

            <div className="flex shrink-0 items-center gap-2">
              <button
                type="button"
                onClick={onBack}
                className="flex h-8 items-center gap-1.5 rounded-md border border-border/70 bg-background/60 px-2.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground"
              >
                <ArrowLeft className="h-3.5 w-3.5" /> Quay lại
              </button>
              <LanguageSwitcher />
            </div>
          </div>
        </header>

        <main className="mx-auto w-full max-w-[1200px] px-3 py-4 md:px-6">
          <div className="mb-4 flex items-center gap-2">
            <Printer className="h-4 w-4 text-primary" />
            <p className="text-xs text-muted-foreground">
              Tạo và in chứng từ doanh nghiệp từ dữ liệu Excel. Dưới đây là hướng dẫn đầy đủ từ A đến Z.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            {/* Quy trình A-Z */}
            <Section icon={ListOrdered} title="Quy trình sử dụng (A–Z)">
              <ol className="list-decimal space-y-1.5 pl-5 text-xs">
                <li><strong>Tải file Excel</strong> — chọn file chứa dữ liệu (đơn hàng, sản phẩm, nhân viên…).</li>
                <li><strong>Chọn sheet & cột nhóm</strong> — chọn sheet dữ liệu và cột dùng để nhóm thành từng phiếu (ví dụ: Mã đơn hàng).</li>
                <li><strong>Chọn mẫu template</strong> — bấm <strong>Chọn Template</strong> để dùng mẫu có sẵn, hoặc <strong>Tải Template</strong> để dùng file của bạn.</li>
                <li><strong>Ghép cột tự động</strong> — hệ thống tự khớp cột Excel với trường <Code>{"{{tên}}"}</Code> trong template; bạn có thể chỉnh lại.</li>
                <li><strong>Xem trước & chọn phiếu</strong> — kiểm tra từng phiếu, tích chọn những phiếu cần in.</li>
                <li><strong>In / Xuất PDF / Xuất ZIP</strong> — in trực tiếp, tải PDF, hoặc tải từng phiếu thành file PDF trong một file ZIP.</li>
              </ol>
            </Section>

            {/* Cú pháp template */}
            <Section icon={Braces} title="Cú pháp template">
              <p className="text-xs text-muted-foreground">
                Template là file Word (.docx) chứa các trường dữ liệu dạng <Code>{"{{tên}}"}</Code>. Hệ thống tự điền giá trị từ Excel vào các trường này. Có thể dùng <Code>{"[[tên]]"}</Code> thay cho <Code>{"{{tên}}"}</Code> — hệ thống tự nhận diện cả hai.
              </p>
              <div className="space-y-1.5">
                <SyntaxRow token="Số PO: {{Số PO}}" desc="Trường dữ liệu — điền giá trị từ cột Excel cùng tên." />
                <SyntaxRow token="{{#items}} … {{/items}}" desc="Vòng lặp bảng — lặp lại một hàng cho mỗi dòng dữ liệu." />
                <SyntaxRow token="{{PAGE}} / {{NUMPAGES}}" desc="Số trang hiện tại / tổng số trang (tự cập nhật khi in)." />
                <SyntaxRow token="{{SUM:Số lượng}}" desc="Tổng cộng cột — cộng tất cả giá trị của trường trong phiếu." />
                <SyntaxRow token="{{TEXT:Tổng tiền}}" desc="Số → chữ — in số thành chữ tiếng Việt (kế toán / hóa đơn)." />
                <SyntaxRow token="{{TEXT:SUM:Thành tiền}}" desc={'Số → chữ của tổng cột — "Số tiền bằng chữ" cho hóa đơn.'} />
              </div>
            </Section>

            {/* Các mẫu có sẵn */}
            <Section icon={Package} title="Các mẫu có sẵn">
              <p className="text-xs text-muted-foreground">
                Bấm <strong>Chọn Template</strong> trong bước 2 để mở danh sách mẫu có bản xem trước. Có 7 mẫu chuẩn doanh nghiệp:
              </p>
              <ul className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
                {PRESET_TEMPLATES.map((p) => {
                  const Icon = presetIcon(p.id);
                  return (
                    <li key={p.id} className="flex items-center gap-2 rounded-md border bg-muted/20 px-2.5 py-1.5">
                      <Icon className="h-3.5 w-3.5 shrink-0 text-primary" />
                      <span className="text-xs font-medium">{p.name}</span>
                    </li>
                  );
                })}
              </ul>
              <p className="text-xs text-muted-foreground">
                Các mẫu đã tự gán <strong>format số</strong> cho cột tiền tệ (₫), số lượng và
                phần trăm — bạn vẫn có thể đổi lại bằng nút <strong>Số</strong> trong Soạn Thảo.
              </p>
            </Section>

            {/* Chỉnh sửa template */}
            <Section icon={Type} title="Chỉnh sửa template (Soạn Thảo)">
              <ul className="list-disc space-y-1.5 pl-5 text-xs">
                <li><strong>Soạn Thảo</strong> — bật chế độ sửa: click vào chữ để sửa nội dung trực tiếp.</li>
                <li><strong>Thêm văn bản</strong> — thêm dòng chữ mới, kéo để định vị, chọn để chỉnh cỡ/màu/căn.</li>
                <li><strong>Chèn ảnh</strong> — thêm logo/chữ ký (nhúng base64, giữ nguyên khi in/PDF).</li>
                <li><strong>Canh khối</strong> — chọn 1 đoạn/khối rồi bấm <strong>Canh khối</strong> để đẩy khối sang <em>trái / giữa / phải</em> của trang giấy.</li>
                <li><strong>Format số</strong> — chọn 1 trường dữ liệu <Code>{"{{tên}}"}</Code>, bấm nút <strong>Số</strong> để chọn định dạng: <em>Số nguyên, Số thập phân, Tiền tệ ₫, Phần trăm %</em> hoặc Mặc định.</li>
                <li><strong>Bảng dữ liệu</strong> — thêm bảng mới, thêm/xóa hàng-cột, đổi màu nền, đường viền.</li>
                <li><strong>Nhân đôi / Xóa trang</strong> — trên tab trang, hover để nhân đôi hoặc xóa.</li>
                <li><strong>Lưu</strong> — ghi chỉnh sửa vào file Word, rồi bấm <strong>Lưu & Xem Kết Quả</strong>.</li>
              </ul>
            </Section>

            {/* Mẹo & lưu ý */}
            <Section icon={Hash} title="Mẹo & lưu ý">
              <ul className="list-disc space-y-1.5 pl-5 text-xs">
                <li>Tên trường <Code>{"{{tên}}"}</Code> nên khớp chính xác với tên cột Excel để tự ghép nhanh.</li>
                <li>Một phiếu = một nhóm theo cột nhóm đã chọn (ví dụ: mỗi Mã đơn hàng là một phiếu).</li>
                <li>Dùng <Code>{"{{SUM:…}}"}</Code> để tự cộng số lượng/thành tiền trong phiếu.</li>
                <li>Dùng <Code>{"{{TEXT:…}}"}</Code> để in <strong>số thành chữ tiếng Việt</strong> — ví dụ <Code>{"{{TEXT:Tổng tiền}}"}</Code> → "một triệu hai trăm…".</li>
                <li>Dùng <Code>{"{{TEXT:SUM:…}}"}</Code> để in chữ của <strong>tổng cột</strong> — dùng cho dòng "Số tiền bằng chữ" trên hóa đơn/phiếu thu chi.</li>
                <li>Ảnh chèn vào template được nhúng base64 — không cần giữ file gốc khi in.</li>
                <li>Lịch sử in gần đây được lưu trên máy (localStorage) để xem lại.</li>
              </ul>
            </Section>

            {/* Bảng ký hiệu */}
            <Section icon={Table2} title="Bảng ký hiệu nhanh">
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b text-left text-muted-foreground">
                      <th className="py-1.5 pr-3 font-medium">Ký hiệu</th>
                      <th className="py-1.5 font-medium">Ý nghĩa</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b"><td className="py-1.5 pr-3"><Code>{"{{Tên}}"}</Code></td><td>Trường dữ liệu</td></tr>
                    <tr className="border-b"><td className="py-1.5 pr-3"><Code>{"{{#items}}"}</Code></td><td>Bắt đầu vòng lặp bảng</td></tr>
                    <tr className="border-b"><td className="py-1.5 pr-3"><Code>{"{{/items}}"}</Code></td><td>Kết thúc vòng lặp bảng</td></tr>
                    <tr className="border-b"><td className="py-1.5 pr-3"><Code>{"{{PAGE}}"}</Code></td><td>Số trang hiện tại</td></tr>
                    <tr className="border-b"><td className="py-1.5 pr-3"><Code>{"{{NUMPAGES}}"}</Code></td><td>Tổng số trang</td></tr>
                    <tr className="border-b"><td className="py-1.5 pr-3"><Code>{"{{SUM:Cột}}"}</Code></td><td>Tổng cộng cột</td></tr>
                    <tr className="border-b"><td className="py-1.5 pr-3"><Code>{"{{TEXT:Cột}}"}</Code></td><td>Số → chữ tiếng Việt</td></tr>
                    <tr><td className="py-1.5 pr-3"><Code>{"{{TEXT:SUM:Cột}}"}</Code></td><td>Tổng cột → chữ ("Số tiền bằng chữ")</td></tr>
                  </tbody>
                </table>
              </div>
            </Section>
          </div>
        </main>

        <footer className="border-t border-border/60 py-3">
          <div className="mx-auto flex w-full max-w-[1200px] flex-col items-center justify-between gap-1 px-3 text-center text-[11px] text-muted-foreground md:flex-row md:px-6">
            <p>© {new Date().getFullYear()} BizBox ERP. Bảo lưu mọi quyền.</p>
            <p className="flex items-center gap-1">
              <Building2 className="h-3 w-3 opacity-60" />
              BizBox.Asia — Giải pháp quản trị doanh nghiệp
            </p>
          </div>
        </footer>
      </div>
    </TooltipProvider>
  );
}
